/**
 * As contas por trás da identidade visual de uma marca (07/10/2026, A075):
 * o degradê do cadastro, o contraste entre cores e a leitura do logo (se
 * ele tem fundo próprio, para o editor gravar e para gerar a versão branca).
 *
 * Moram aqui, e não em cada tela, porque a prévia do editor de identidade
 * precisa da MESMA conta que a tela real usa — senão a prévia mente.
 */
import { tint } from './tokens';

/**
 * O degradê do topo do cadastro, como no protótipo: a primária no topo, a
 * primária com 49% de branco a 46,54% da altura e com 81% de branco na base.
 * Para a Eva Maternidade (#78C0D7) dá #BADFEB e #E5F3F7, contra #BBDEEA e
 * #E4F5FB do Figma — as proporções são as que mais se aproximam das cores
 * escolhidas lá.
 *
 * Os três tons saem só da primária. Antes, meio e base eram os tons 4 e 2 da
 * escala da marca, e o tom 2 é substituído pela cor secundária quando a
 * marca tem uma: a Eva Maternidade tem secundária branca, e o degradê
 * terminava em #FFFFFF, mais claro que o fundo, apagando a borda de baixo.
 */
export function degradeDoCadastro(primaria: string): string {
  return `linear-gradient(180deg, ${primaria} 0%, ${tint(primaria, 0.49)} 46.54%, ${tomClaroDaMarca(primaria)} 100%)`;
}

/**
 * O tom claro da marca (A087): a primária com 81% de branco. É a base do
 * degradê do cadastro e o fim do degradê do onboarding — no Figma da Eva
 * Maternidade as duas são #E4F5FB; daqui sai #E5F3F7. Calculado só da
 * primária, de propósito: o tom 2 da escala é trocado pela cor secundária, e
 * a secundária branca da Eva Maternidade apagava o degradê e o círculo da
 * tela de conclusão.
 */
export function tomClaroDaMarca(primaria: string): string {
  return tint(primaria, 0.81);
}

/**
 * O degradê do onboarding (A087): branco no topo até o tom claro da marca
 * embaixo, como no protótipo ("2.2 Boas vindas": #FFFFFF → #E4F5FB). Antes
 * terminava no tom 2, que vira a secundária — branco a branco, chapado, na
 * Eva Maternidade.
 */
export function degradeDoOnboarding(primaria: string): string {
  return `linear-gradient(180deg, #FFFFFF 0%, ${tomClaroDaMarca(primaria)} 100%)`;
}

/**
 * A cor da roupa da personagem que acena na abertura do onboarding (A091).
 * Há dez versões do mesmo vídeo — a mesma animação, só a roupa recolorida,
 * em tons pastel de mesma claridade — e a marca usa a da família da sua
 * primária: o matiz decide a família, e a roupa nunca é a primária em si
 * (um magenta forte numa roupa brigaria com o botão).
 *
 * Primária quase sem cor (preto, cinza, branco) fica com a cinza; terrosa
 * (laranja ou amarelo pouco saturado, ou escuro: marrom, caramelo), com a
 * bege. Sem primária válida, a azul — a original.
 */
export const CORES_DA_BONECA = ['rosa', 'coral', 'pessego', 'amarelo', 'verde', 'verde-agua', 'azul', 'lilas', 'bege', 'cinza'] as const;
export type CorDaBoneca = (typeof CORES_DA_BONECA)[number];

/** Onde começa cada família, pelo matiz da primária (graus), em ordem. */
const FAMILIAS_POR_MATIZ: Array<[number, CorDaBoneca]> = [
  [15, 'pessego'],
  [40, 'amarelo'],
  [70, 'verde'],
  [155, 'verde-agua'],
  // Azul a partir de 188°, e não 190: a Eva Maternidade (#78C0D7, 194°) fica
  // no azul, que já combinava com ela.
  [188, 'azul'],
  [250, 'lilas'],
  [300, 'rosa'],
  [345, 'coral'],
];

export function corDaBoneca(primaria: string | null | undefined): CorDaBoneca {
  const c = primaria ? rgb(primaria) : null;
  if (!c) return 'azul';
  const [r, g, b] = c.map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (s < 0.12 || l < 0.08 || l > 0.95) return 'cinza';
  let h = 0;
  if (max === r) h = 60 * (((g - b) / d) % 6);
  else if (max === g) h = 60 * ((b - r) / d + 2);
  else h = 60 * ((r - g) / d + 4);
  if (h < 0) h += 360;
  if (h >= 15 && h < 60 && (s < 0.45 || l < 0.3)) return 'bege';
  let familia: CorDaBoneca = 'coral';
  for (const [inicio, nome] of FAMILIAS_POR_MATIZ) if (h >= inicio) familia = nome;
  return h < 15 ? 'coral' : familia;
}

function rgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function hex([r, g, b]: [number, number, number]): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`.toUpperCase();
}

/** Luminância relativa (WCAG). */
export function luminancia(cor: string): number {
  const c = rgb(cor);
  if (!c) return 1;
  const [r, g, b] = c.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Razão de contraste (WCAG), de 1 a 21. */
export function contraste(a: string, b: string): number {
  const x = luminancia(a);
  const y = luminancia(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

export interface AnaliseDoLogo {
  /** O logo cobre o próprio quadro (JPEG, PNG com fundo): aparece como um bloco. */
  temFundo: boolean;
  /** A cor do fundo do próprio logo, quando ele tem um. */
  corDoFundo: string | null;
  /** A cor média do desenho (o que não é fundo nem transparente). */
  corDoDesenho: string;
  /** Largura ÷ altura. */
  proporcao: number;
}

/**
 * Lê os pixels do logo para saber se ele tem fundo próprio e qual a cor do
 * desenho. Precisa que o servidor da imagem libere a leitura (CORS); se não
 * liberar, ou a imagem não carregar, devolve `null` — e a prévia mostra os
 * fundos sem o selo de contraste.
 */
export function analisarLogo(url: string): Promise<AnaliseDoLogo | null> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = () => resolve(null);
    img.onload = () => {
      try {
        const escala = Math.min(1, 160 / Math.max(img.naturalWidth, img.naturalHeight));
        const w = Math.max(1, Math.round(img.naturalWidth * escala));
        const h = Math.max(1, Math.round(img.naturalHeight * escala));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, w, h);
        const d = ctx.getImageData(0, 0, w, h).data;
        const px = (x: number, y: number) => {
          const i = (y * w + x) * 4;
          return [d[i], d[i + 1], d[i + 2], d[i + 3]] as const;
        };

        // A borda diz se há fundo: opaca quase toda, e de cor uniforme.
        const borda: Array<readonly [number, number, number, number]> = [];
        for (let x = 0; x < w; x++) borda.push(px(x, 0), px(x, h - 1));
        for (let y = 0; y < h; y++) borda.push(px(0, y), px(w - 1, y));
        const opacasNaBorda = borda.filter((p) => p[3] > 200);
        const temFundo = opacasNaBorda.length / borda.length > 0.9;
        const media = (ps: ReadonlyArray<readonly [number, number, number, number]>): [number, number, number] => {
          const s = ps.reduce((a, p) => [a[0] + p[0], a[1] + p[1], a[2] + p[2]], [0, 0, 0]);
          return [s[0] / ps.length, s[1] / ps.length, s[2] / ps.length];
        };
        const fundo = temFundo ? media(opacasNaBorda) : null;

        // O desenho: pixels opacos que não são o fundo do próprio logo.
        const desenho: Array<readonly [number, number, number, number]> = [];
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const p = px(x, y);
            if (p[3] < 128) continue;
            if (fundo && Math.hypot(p[0] - fundo[0], p[1] - fundo[1], p[2] - fundo[2]) < 48) continue;
            desenho.push(p);
          }
        }
        const corDoDesenho = desenho.length ? hex(media(desenho)) : fundo ? hex(fundo) : '#000000';
        resolve({ temFundo, corDoFundo: fundo ? hex(fundo) : null, corDoDesenho, proporcao: img.naturalWidth / img.naturalHeight });
      } catch {
        // Canvas "contaminado": o servidor não liberou a leitura.
        resolve(null);
      }
    };
    img.src = url;
  });
}

/* ---------- Os logos da marca por fundo (A076) ---------- */

export interface LogosDoBranding {
  logoUrl?: string | null;
  logoSobreCorUrl?: string | null;
  simboloUrl?: string | null;
  logoHasOwnBackground?: boolean;
}

/**
 * O logo de cada fundo: em fundo colorido ou escuro, a versão para fundo
 * colorido, se a marca tiver uma; senão, o logo de sempre.
 */
export function logoParaFundo(b: LogosDoBranding | null | undefined, fundo: 'claro' | 'cor'): { url: string | null; sobreCor: boolean } {
  if (fundo === 'cor' && b?.logoSobreCorUrl) return { url: b.logoSobreCorUrl, sobreCor: true };
  return { url: b?.logoUrl || null, sobreCor: false };
}

/** O que vai num círculo ou num quadrado (avatar, ícone): o símbolo, ou o logo. */
export function simboloDaMarca(b: LogosDoBranding | null | undefined): string | null {
  return b?.simboloUrl || b?.logoUrl || null;
}

function carregarImagem(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function paraBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b), 'image/png');
    } catch {
      resolve(null);
    }
  });
}

/**
 * A versão branca do logo: todo pixel que não é transparente vira branco,
 * mantendo a transparência. Só faz sentido para logo sem fundo próprio —
 * num logo com fundo, sairia um retângulo branco. `null` se não der para ler
 * a imagem.
 */
export async function gerarVersaoBranca(url: string): Promise<Blob | null> {
  const img = await carregarImagem(url);
  if (!img) return null;
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  try {
    ctx.drawImage(img, 0, 0);
    const dados = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = dados.data;
    for (let i = 0; i < d.length; i += 4) {
      d[i] = 255;
      d[i + 1] = 255;
      d[i + 2] = 255;
    }
    ctx.putImageData(dados, 0, 0);
  } catch {
    return null;
  }
  return paraBlob(canvas);
}

export type IconesDoApp = Record<'icon192' | 'icon512' | 'iconMaskable192' | 'iconMaskable512' | 'iosIcon', Blob>;

/**
 * Os cinco ícones do app no celular, a partir do símbolo (ou do logo), SEM
 * esticar: a imagem é encaixada no quadrado mantendo a proporção. Antes, o
 * gerador do painel antigo desenhava o logo no quadrado inteiro, e um logo
 * largo saía achatado.
 *
 * - comuns (192, 512): fundo transparente, com uma folga de 6%;
 * - "maskable" (Android recorta em círculo ou gota): fundo branco, e a
 *   imagem dentro da área segura (60% do centro);
 * - iPhone (180): fundo branco, folga de 12%.
 */
export async function gerarIconesDoApp(url: string): Promise<IconesDoApp | null> {
  const img = await carregarImagem(url);
  if (!img) return null;
  const desenhar = async (lado: number, fracao: number, fundoBranco: boolean): Promise<Blob | null> => {
    const canvas = document.createElement('canvas');
    canvas.width = lado;
    canvas.height = lado;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    if (fundoBranco) {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, lado, lado);
    }
    const caixa = lado * fracao;
    const escala = Math.min(caixa / img.naturalWidth, caixa / img.naturalHeight);
    const w = img.naturalWidth * escala;
    const h = img.naturalHeight * escala;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, (lado - w) / 2, (lado - h) / 2, w, h);
    return paraBlob(canvas);
  };
  try {
    const [icon192, icon512, iconMaskable192, iconMaskable512, iosIcon] = await Promise.all([
      desenhar(192, 0.88, false),
      desenhar(512, 0.88, false),
      desenhar(192, 0.6, true),
      desenhar(512, 0.6, true),
      desenhar(180, 0.76, true),
    ]);
    if (!icon192 || !icon512 || !iconMaskable192 || !iconMaskable512 || !iosIcon) return null;
    return { icon192, icon512, iconMaskable192, iconMaskable512, iosIcon };
  } catch {
    // Canvas contaminado: o servidor da imagem não liberou a leitura.
    return null;
  }
}
